import { useState, useEffect, useMemo } from 'react'
import { X, Layers, Radio, Film, AlertCircle, CheckCircle, ExternalLink } from 'lucide-react'
import { renderTemplate } from './templates'
import { renderThumb, canvasToBlob, uploadThumb, thumbFilename } from './thumbRender'
import TagPicker from './TagPicker'

// "Schedule selected games" dialog. Creates one catalog item per game via the
// worker (same path as Catalog → Create), as a Live event or an On-Demand item.
// Titles/descriptions come from the league template, editable for this batch.
// If a picked game is half of a double header whose partner isn't picked, the
// partner is offered here so both can go in together.
//
// props: season, picked[] (games to schedule), allGames[], isDh(g), templates[],
//        ctxFor(g), imageOf(name) → url|'', leagueLogo, thumbTemplates[], sourcesFor(g), api,
//        onDone({ created, skipped, errors }), onClose
export default function ScheduleModal({ season, picked, allGames, isDh, templates, ctxFor, imageOf = () => '', leagueLogo = '', thumbTemplates = [], sourcesFor = () => ({}), api, onDone, onClose }) {
  const defaultTpl = templates.find(t => t.is_default) || templates[0] || null
  const [mode, setMode] = useState('live')
  const [tplId, setTplId] = useState(defaultTpl?.id ?? '')
  const [titleTpl, setTitleTpl] = useState(defaultTpl?.title_template || '<date> - <away-team> vs <home-team>')
  const [descTpl, setDescTpl] = useState(defaultTpl?.description_template || '')
  const [extraIds, setExtraIds] = useState(() => new Set())   // DH partners opted in
  const [excluded, setExcluded] = useState(() => new Set())   // games unticked in the preview list
  // Last-used settings for this league (input, channel, status, tags, duration…) come back next time.
  const memKey = `teamsched.schedule.${season?.league || 'league'}`
  const remembered = useMemo(() => { try { return JSON.parse(localStorage.getItem(memKey) || '{}') } catch { return {} } }, [memKey])
  const [duration, setDuration] = useState(remembered.duration ?? 150)
  const [status, setStatus] = useState(remembered.status === 'active' ? 'active' : 'draft')
  const [includeInSchedule, setIncludeInSchedule] = useState(remembered.includeInSchedule ?? true)
  const [liveInputId, setLiveInputId] = useState(remembered.liveInputId ?? '')
  const [channelId, setChannelId] = useState(remembered.channelId ?? '')
  const [tags, setTags] = useState(() => {
    const r = remembered.tags
    if (Array.isArray(r)) return r
    if (typeof r === 'string') return r.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
    return [season?.league, season?.sport].filter(Boolean).map(s => s.toLowerCase())
  })
  const [thumb, setThumb] = useState('')
  const defaultThumbTpl = thumbTemplates.find(t => t.is_default) || thumbTemplates[0] || null
  const [thumbSrc, setThumbSrc] = useState(defaultThumbTpl ? 'template' : 'home')   // template | home | away | league | custom | none  (not remembered: template availability decides)
  const [thumbTplId, setThumbTplId] = useState(defaultThumbTpl?.id ?? '')
  const [previews, setPreviews] = useState({})     // game_id → data URL (template mode)
  const [progress, setProgress] = useState('')
  const [mediaUrl, setMediaUrl] = useState('')
  const [liveInputs, setLiveInputs] = useState([])
  const [channels, setChannels] = useState([])
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [err, setErr] = useState('')

  useEffect(() => {
    api.listLiveInputs().then(d => setLiveInputs(d.liveInputs || [])).catch(() => {})
    api.listChannels().then(d => setChannels(d.channels || [])).catch(() => {})
  }, [api])

  function pickTemplate(id) {
    setTplId(id)
    const t = templates.find(x => String(x.id) === String(id))
    if (t) { setTitleTpl(t.title_template); setDescTpl(t.description_template || '') }
  }

  // Double-header partners: same date + rink as a picked game, not picked, not already in the catalog.
  const partners = useMemo(() => {
    const pickedIds = new Set(picked.map(g => g.game_id))
    const out = new Map()
    picked.filter(isDh).forEach(g => {
      allGames
        .filter(o => o.game_date === g.game_date && o.location === g.location && !pickedIds.has(o.game_id) && !o.tbtv_id && o.status !== 'removed')
        .forEach(o => { if (!out.has(o.game_id)) out.set(o.game_id, { partner: o, of: g }) })
    })
    return [...out.values()]
  }, [picked, allGames, isDh])

  // Partners are offered unchecked; one click takes them all.
  useEffect(() => { setExtraIds(new Set()) }, [partners])
  const allPartnersOn = partners.length > 0 && partners.every(p => extraIds.has(p.partner.game_id))

  const batch = useMemo(() => {
    const extras = partners.filter(p => extraIds.has(p.partner.game_id)).map(p => p.partner)
    return [...picked, ...extras].sort((a, b) => (a.game_date + (a.start_at || '')).localeCompare(b.game_date + (b.start_at || '')))
  }, [picked, partners, extraIds])

  const thumbTpl = thumbTemplates.find(t => String(t.id) === String(thumbTplId)) || null
  const thumbFor = (g) => {
    if (thumbSrc === 'template') return previews[g.game_id] || ''
    if (thumbSrc === 'home') return imageOf(g.home) || imageOf(g.visitor) || leagueLogo || ''
    if (thumbSrc === 'away') return imageOf(g.visitor) || imageOf(g.home) || leagueLogo || ''
    if (thumbSrc === 'league') return leagueLogo || ''
    if (thumbSrc === 'custom') return thumb.trim()
    return ''
  }
  const rendered = useMemo(() => batch.map(g => {
    const ctx = ctxFor(g)
    return { g, title: renderTemplate(titleTpl, ctx), desc: renderTemplate(descTpl, ctx), thumb: thumbFor(g), already: !!g.tbtv_id, noStart: mode === 'live' && !g.start_at }
  }), [batch, titleTpl, descTpl, ctxFor, mode, thumbSrc, thumb, imageOf, leagueLogo, previews])  // eslint-disable-line react-hooks/exhaustive-deps

  const eligible = rendered.filter(r => !r.already && !r.noStart)
  const toCreate = eligible.filter(r => !excluded.has(r.g.game_id))
  const allOn = eligible.length > 0 && toCreate.length === eligible.length
  const toggleExcluded = (id) => setExcluded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  // Template mode: render small previews for the batch (cheap — 480px wide).
  useEffect(() => {
    if (thumbSrc !== 'template' || !thumbTpl) { setPreviews({}); return }
    let cancelled = false
    ;(async () => {
      const out = {}
      for (const g of batch) {
        if (cancelled) return
        try {
          const c = await renderThumb(thumbTpl, { ctx: ctxFor(g), sources: sourcesFor(g) }, { scale: 480 / (thumbTpl.width || 1920) })
          out[g.game_id] = c.toDataURL('image/jpeg', 0.8)
        } catch { out[g.game_id] = '' }
        if (!cancelled) setPreviews({ ...out })
      }
    })()
    return () => { cancelled = true }
  }, [thumbSrc, thumbTpl, batch, ctxFor, sourcesFor])

  async function submit() {
    if (!toCreate.length) return
    if (mode === 'ondemand' && !mediaUrl.trim()) { setErr('On-demand items need a media URL'); return }
    setBusy(true); setErr('')
    try {
      // Template mode: render each card at full size and upload it to Cloudflare Images first.
      const thumbs = {}
      if (thumbSrc === 'template' && thumbTpl) {
        let i = 0
        for (const { g } of toCreate) {
          i++
          setProgress(`Rendering thumbnail ${i} of ${toCreate.length}…`)
          const c = await renderThumb(thumbTpl, { ctx: ctxFor(g), sources: sourcesFor(g) })
          const blob = await canvasToBlob(c)
          const { url } = await uploadThumb(api.apiRaw, blob, thumbFilename(season, g))
          thumbs[g.game_id] = url
        }
        setProgress('Creating catalog items…')
      }
      const ch = channels.find(c => String(c.viloud_id) === String(channelId))
      const r = await api.scheduleToCatalog(season.id, {
        mode,
        items: toCreate.map(({ g, title, desc, thumb: t }) => ({ game_id: g.game_id, title, short_description: desc, long_description: desc, thumbnail_url: thumbs[g.game_id] || (thumbSrc === 'template' ? '' : t) })),
        options: {
          duration_minutes: Number(duration) || 150,
          status,
          include_in_schedule: includeInSchedule,
          cf_live_input_id: liveInputId || null,
          viloud_channel_id: ch?.viloud_id || null,
          viloud_channel_name: ch?.name || null,
          tags,
          thumbnail_url: thumbSrc === 'custom' ? thumb.trim() : '',
          media_url: mediaUrl.trim(),
        },
      })
      try { localStorage.setItem(memKey, JSON.stringify({ duration, status, includeInSchedule, liveInputId, channelId, tags, thumbSrc })) } catch { /* ignore */ }
      setResult(r)
      onDone(r)
    } catch (e) {
      setErr(e.body?.error || e.message)
    } finally {
      setBusy(false); setProgress('')
    }
  }

  return (
    <div className="ts-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose() }}>
      <div className="ts-modal ts-modal-wide" role="dialog" aria-modal="true" aria-labelledby="ts-sch-title">
        <div className="ts-modal-head">
          <h2 id="ts-sch-title">Schedule {batch.length} {batch.length === 1 ? 'game' : 'games'} <span className="muted">· {season.league} · {season.label}</span></h2>
          <button type="button" className="ts-iconbtn" onClick={onClose} aria-label="Close" disabled={busy}><X size={16} /></button>
        </div>

        {result ? (
          <div className="ts-sch-result">
            <p><CheckCircle size={16} className="text-success" /> Created {result.created.length} catalog {result.created.length === 1 ? 'item' : 'items'} as {result.mode === 'live' ? 'Live events' : 'On-Demand'}.</p>
            {result.skipped.length > 0 && <p className="muted">Skipped {result.skipped.length} already in the catalog.</p>}
            {result.errors.length > 0 && (
              <ul className="ts-sch-errors">
                {result.errors.map(e => <li key={e.game_id}><AlertCircle size={13} /> Game {e.game_id}: {e.error}</li>)}
              </ul>
            )}
            <ul className="ts-sch-created">
              {result.created.map(c => (
                <li key={c.tbtv_id}>
                  <a href={`/ondemand/catalog?select=${c.tbtv_id}`} target="_blank" rel="noreferrer">{c.title} <ExternalLink size={11} /></a>
                </li>
              ))}
            </ul>
            <div className="ts-modal-actions"><span className="ts-spacer" /><button type="button" onClick={onClose}>Done</button></div>
          </div>
        ) : (
          <div className="ts-sch-body">
            {/* mode */}
            <div className="ts-seg ts-sch-mode" role="group" aria-label="Catalog item type">
              <button type="button" aria-pressed={mode === 'live'} onClick={() => setMode('live')}><Radio size={14} /> Live event</button>
              <button type="button" aria-pressed={mode === 'ondemand'} onClick={() => setMode('ondemand')}><Film size={14} /> On-Demand</button>
            </div>

            {/* double headers */}
            {partners.length > 0 && (
              <div className="ts-sch-dh">
                <p>
                  <Layers size={14} /> <strong>Double header{partners.length > 1 ? 's' : ''} available.</strong> These games share a rink and a night with a selected game but aren't selected. Schedule them too?
                  <span className="ts-spacer" />
                  <button type="button" className="ts-chip" onClick={() => setExtraIds(allPartnersOn ? new Set() : new Set(partners.map(p => p.partner.game_id)))}>
                    {allPartnersOn ? 'Deselect all' : 'Select all'}
                  </button>
                </p>
                {partners.map(({ partner: p, of }) => (
                  <label key={p.game_id} className="ts-check">
                    <input type="checkbox" checked={extraIds.has(p.game_id)} onChange={e => {
                      const next = new Set(extraIds); e.target.checked ? next.add(p.game_id) : next.delete(p.game_id); setExtraIds(next)
                    }} />
                    <span>{p.game_date} · {(p.game_time || '').replace(/\s*E[DS]T$/, '')} · <strong>{p.visitor} at {p.home}</strong> <span className="muted">· {p.location} · pairs with {of.visitor} at {of.home}</span></span>
                  </label>
                ))}
              </div>
            )}

            {/* templates */}
            <h3 className="ts-sch-h">Title &amp; description</h3>
            <div className="ts-sch-grid">
              <div className="form-group">
                <label htmlFor="ts-sch-tpl">Template</label>
                <select id="ts-sch-tpl" value={tplId} onChange={e => pickTemplate(e.target.value)}>
                  {!templates.length && <option value="">No saved templates</option>}
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (default)' : ''}</option>)}
                </select>
              </div>
              <div className="form-group ts-span-2">
                <label htmlFor="ts-sch-title-tpl">Title <span className="muted">(edits apply to this batch only)</span></label>
                <input id="ts-sch-title-tpl" value={titleTpl} onChange={e => setTitleTpl(e.target.value)} />
              </div>
              <div className="form-group ts-span-3">
                <label htmlFor="ts-sch-desc-tpl">Description</label>
                <textarea id="ts-sch-desc-tpl" rows={2} value={descTpl} onChange={e => setDescTpl(e.target.value)} />
              </div>
            </div>

            {/* options */}
            <h3 className="ts-sch-h">{mode === 'live' ? 'Live event settings' : 'On-Demand settings'} <span className="muted">· remembered for {season.league}</span></h3>
            <div className="ts-sch-grid">
              {mode === 'live' ? (
                <>
                  <div className="form-group">
                    <label htmlFor="ts-sch-dur">Duration (min)</label>
                    <input id="ts-sch-dur" type="number" inputMode="numeric" min="1" value={duration} onChange={e => setDuration(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="ts-sch-input">CF Live Input</label>
                    <select id="ts-sch-input" value={liveInputId} onChange={e => setLiveInputId(e.target.value)}>
                      <option value="">— none yet —</option>
                      {liveInputs.map(li => <option key={li.uid} value={li.uid}>{li.meta?.name || li.uid}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="ts-sch-channel">TBTV Channel</label>
                    <select id="ts-sch-channel" value={channelId} onChange={e => setChannelId(e.target.value)}>
                      <option value="">— none —</option>
                      {channels.map(c => <option key={c.viloud_id || c.tbtv_channel_id} value={c.viloud_id}>{c.name}</option>)}
                    </select>
                  </div>
                </>
              ) : (
                <div className="form-group ts-span-3">
                  <label htmlFor="ts-sch-media">Media URL <span className="muted">(applied to every item — HLS or YouTube embed)</span></label>
                  <input id="ts-sch-media" type="url" inputMode="url" value={mediaUrl} onChange={e => setMediaUrl(e.target.value)} placeholder="https://…/manifest/video.m3u8" required />
                </div>
              )}
              <div className="form-group">
                <label htmlFor="ts-sch-status">Status</label>
                <select id="ts-sch-status" value={status} onChange={e => setStatus(e.target.value)}>
                  <option value="draft">Draft (not on watch)</option>
                  <option value="active">Active (publish to watch)</option>
                </select>
              </div>
              <div className="form-group ts-span-3">
                <label htmlFor="ts-sch-tags">Tags</label>
                <TagPicker id="ts-sch-tags" value={tags} onChange={setTags} api={api} />
              </div>
              <div className="form-group">
                <label htmlFor="ts-sch-thumbsrc">Thumbnail</label>
                <select id="ts-sch-thumbsrc" value={thumbSrc} onChange={e => setThumbSrc(e.target.value)}>
                  <option value="template" disabled={!thumbTemplates.length}>Generate from template{thumbTemplates.length ? '' : ' (none yet)'}</option>
                  <option value="home">Home team logo</option>
                  <option value="away">Away team logo</option>
                  <option value="league" disabled={!leagueLogo}>League logo{leagueLogo ? '' : ' (none set)'}</option>
                  <option value="custom">Custom URL</option>
                  <option value="none">None</option>
                </select>
              </div>
              {thumbSrc === 'template' && thumbTemplates.length > 1 && (
                <div className="form-group">
                  <label htmlFor="ts-sch-thumbtpl">Thumbnail template</label>
                  <select id="ts-sch-thumbtpl" value={thumbTplId} onChange={e => setThumbTplId(e.target.value)}>
                    {thumbTemplates.map(t => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (default)' : ''}</option>)}
                  </select>
                </div>
              )}
              {thumbSrc === 'custom' && (
                <div className="form-group ts-span-3">
                  <label htmlFor="ts-sch-thumb">Thumbnail URL</label>
                  <input id="ts-sch-thumb" type="url" inputMode="url" value={thumb} onChange={e => setThumb(e.target.value)} placeholder="https://imagedelivery.net/…/public" />
                </div>
              )}
              {mode === 'live' && (
                <label className="ts-check ts-span-3">
                  <input type="checkbox" checked={includeInSchedule} onChange={e => setIncludeInSchedule(e.target.checked)} />
                  <span>Include in Live Schedule (watch.tenband.tv)</span>
                </label>
              )}
            </div>

            {/* what will be created */}
            <p className="ts-sch-summary">
              Creating as <strong>{mode === 'live' ? 'Live events' : 'On-Demand'}</strong> · <strong>{status}</strong>
              {mode === 'live' && <> · input <strong>{liveInputs.find(li => li.uid === liveInputId)?.meta?.name || liveInputId || 'none'}</strong> · channel <strong>{channels.find(c => String(c.viloud_id) === String(channelId))?.name || 'none'}</strong> · {duration} min · {includeInSchedule ? 'in' : 'not in'} Live Schedule</>}
              {tags.length > 0 && <> · tags <strong>{tags.join(', ')}</strong></>}
            </p>

            {/* preview */}
            <h3 className="ts-sch-h">Games</h3>
            <div className="ts-sch-preview">
              <div className="ts-sch-previewhead">
                <button type="button" className="ts-chip" onClick={() => setExcluded(allOn ? new Set(eligible.map(r => r.g.game_id)) : new Set())} disabled={!eligible.length}>
                  {allOn ? 'Deselect all' : 'Select all'}
                </button>
                <span className="muted">
                  {toCreate.length} of {eligible.length} to create
                  {rendered.length - eligible.length ? ` · ${rendered.length - eligible.length} skipped` : ''}
                </span>
              </div>
              {rendered.map(r => (
                <div key={r.g.game_id} className={`ts-sch-item ${r.already || r.noStart ? 'is-skip' : ''} ${excluded.has(r.g.game_id) ? 'is-off' : ''}`}>
                  {!r.already && !r.noStart && (
                    <label className="ts-sch-item-pick" title={excluded.has(r.g.game_id) ? 'Include this game' : 'Leave this game out'}>
                      <input type="checkbox" checked={!excluded.has(r.g.game_id)} onChange={() => toggleExcluded(r.g.game_id)} aria-label={`Create ${r.g.visitor} at ${r.g.home}`} />
                    </label>
                  )}
                  {r.thumb && <img className={`ts-sch-item-thumb ${thumbSrc === 'template' ? 'is-wide' : ''}`} src={r.thumb} alt="" loading="lazy" />}
                  <div className="ts-sch-item-title">{r.title || <span className="muted">— empty title —</span>}</div>
                  {r.desc && <div className="ts-sch-item-desc">{r.desc}</div>}
                  {r.already && <div className="ts-sch-item-note">Already in catalog — skipped</div>}
                  {r.noStart && <div className="ts-sch-item-note">No start time on this game — can't create a live event</div>}
                </div>
              ))}
            </div>

            {err && <p className="ts-tpl-warn"><AlertCircle size={13} /> {err}</p>}

            <div className="ts-modal-actions">
              <button type="button" onClick={onClose} disabled={busy}>Cancel</button>
              <span className="ts-spacer" />
              <button type="button" onClick={submit} disabled={busy || !toCreate.length}>
                {busy ? (progress || 'Creating…') : `Create ${toCreate.length} ${mode === 'live' ? 'live event' : 'on-demand item'}${toCreate.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
